import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown, AlertCircle, Loader2, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  InputOTP,
  InputOTPGroup,
  InputOTPSlot 
} from '@/components/ui/input-otp';
import {
  sendPhoneVerificationCode,
  verifyPhoneCode,
  unlinkPhoneProvider,
} from '@/lib/phoneAuth';
import { getAllCountries, CountryOption, formatAsYouType, isValidPhoneNumber } from '@/lib/phoneUtils';
import { CountryCode } from 'libphonenumber-js';
import { ConfirmationResult } from 'firebase/auth';
import { useAuth } from '@/hooks/useAuth';
import { config } from '@/config/environment';
import { useNotificationPreferencesContext } from '@/context/NotificationPreferencesContext';

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (phoneNumber: string) => void;
  currentPhoneNumber?: string | null;
}

export const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({
  isOpen,
  onClose,
  onVerified = () => {},
  currentPhoneNumber,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(currentPhoneNumber || '');
  const [countryCode, setCountryCode] = useState<CountryCode>('US');
  const [tempCountryCode, setTempCountryCode] = useState<CountryCode>('US');
  const [countryDialCode, setCountryDialCode] = useState('+1');
  const [tempCountryDialCode, setTempCountryDialCode] = useState('+1');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);
  const [showWhatsAppStep, setShowWhatsAppStep] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCountrySelectOpen, setIsCountrySelectOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { updateWhatsAppPreferences } = useNotificationPreferencesContext();
  
  const initialSetupDone = useRef(false);

  const countries = getAllCountries();

  useEffect(() => {
    if (!initialSetupDone.current && isOpen) {
      if (currentPhoneNumber) {
        // Attempt to parse country code and phone number if currentPhoneNumber is provided
        const foundCountry = countries.find((country) =>
          currentPhoneNumber.startsWith(country.value)
        );
        if (foundCountry) {
          setCountryCode(foundCountry.code);
          setTempCountryCode(foundCountry.code);
          setCountryDialCode(foundCountry.value);
          setTempCountryDialCode(foundCountry.value);
          setPhoneNumber(currentPhoneNumber.substring(foundCountry.value.length));
        } else {
          setPhoneNumber(currentPhoneNumber);
        }
      } else {
        // Set default country to US if no current phone number
        const defaultCountry = countries.find((country) => country.code === 'US');
        if (defaultCountry) {
          setCountryCode(defaultCountry.code);
          setTempCountryCode(defaultCountry.code);
          setCountryDialCode(defaultCountry.value);
          setTempCountryDialCode(defaultCountry.value);
        }
      }
      initialSetupDone.current = true;
    }
  }, [currentPhoneNumber, countries, isOpen]);

  // Reset the ref and all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      initialSetupDone.current = false;
      setTimeLeft(0);
      // Reset all modal state to initial values
      setPhoneNumber(currentPhoneNumber || '');
      setVerificationCode('');
      setConfirmationResult(null);
      setIsCodeSent(false);
      setIsVerified(false);
      setShowWhatsAppPrompt(false);
      setShowWhatsAppStep(false);
      setError(null);
      setIsLoading(false);
      setIsCountrySelectOpen(false);
      
      // Reset country codes to default (US)
      const defaultCountry = countries.find((country) => country.code === 'US');
      if (defaultCountry) {
        setCountryCode(defaultCountry.code);
        setTempCountryCode(defaultCountry.code);
        setCountryDialCode(defaultCountry.value);
        setTempCountryDialCode(defaultCountry.value);
      }
    }
  }, [isOpen, currentPhoneNumber, countries]);

  // Manejar el contador de tiempo
  useEffect(() => {
    let timerId: number | undefined;
    
    if (timeLeft > 0) {
      timerId = window.setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    
    return () => {
      if (timerId !== undefined) {
        clearInterval(timerId);
      }
    };
  }, [timeLeft]);

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedNumber = formatAsYouType(e.target.value, countryCode);
    setPhoneNumber(formattedNumber);
    setError(null);
  };

  const handleCountryCodeChange = useCallback((value: CountryCode) => {
    const selectedCountry = countries.find((country) => country.code === value);
    if (selectedCountry) {
      setCountryCode(value);
      setTempCountryCode(value);
      setCountryDialCode(selectedCountry.value);
      setTempCountryDialCode(selectedCountry.value);
      setError(null);
    }
    setIsCountrySelectOpen(false);
  }, [countries]);

  const handleSendCode = async () => {
    setError(null);
    setIsLoading(true);
    try {
      // Validar el número de teléfono antes de enviar el código
      if (!isValidPhoneNumber(phoneNumber, countryCode)) {
        setError('Por favor ingresa un número de teléfono válido');
        setIsLoading(false);
        return;
      }
      
      const fullPhoneNumber = `${countryDialCode}${phoneNumber}`;
      const result = await sendPhoneVerificationCode(fullPhoneNumber);
      setConfirmationResult(result);
      setIsCodeSent(true);
      setTimeLeft(60); // Iniciar contador de 60 segundos
      
      // Activar las notificaciones por WhatsApp en las preferencias del usuario
      try {
        await updateWhatsAppPreferences({
          enabled: true,
          phoneNumber: phoneNumber, // Store only the local number without country code
          countryCode: countryDialCode,
          countryCodeIso: countryCode,
          isVerified: false
        });
      } catch (error) {
        console.error('Error updating notification preferences:', error);
        // No interrumpimos el flujo principal si esto falla
      }
    } catch (err: unknown) {
      console.error('Error sending verification code:', err);
      setError(err instanceof Error ? err.message : 'Failed to send verification code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    setIsLoading(true);
    try {
      if (confirmationResult) {
        await verifyPhoneCode(confirmationResult, verificationCode);
        
        // Actualizar las preferencias del usuario para marcar el número como verificado
        try {
          await updateWhatsAppPreferences({
            isVerified: true,
            phoneNumber: phoneNumber, // Store only the local number without country code
          });
        } catch (error) {
          console.error('Error updating notification preferences:', error);
          // No interrumpimos el flujo principal si esto falla
        }
        
        setIsVerified(true);
        setShowWhatsAppStep(true); // Mostrar el paso final de WhatsApp
        onVerified(`${countryDialCode}${phoneNumber}`);
        // No cerramos el modal automáticamente para permitir que el usuario vea el prompt de WhatsApp
      } else {
        throw new Error('Confirmation result not found.');
      }
    } catch (err: unknown) {
      console.error('Error verifying code:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify code.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const getWhatsAppLink = () => {
    const supportNumber = config.whatsapp.supportNumber;
    const message = encodeURIComponent("Hi, I'd like to initiate WhatsApp notifications for my account.");
    return `https://wa.me/${supportNumber}?text=${message}`;
  };

  const handleUnlinkPhoneNumber = async () => {
    setError(null);
    setIsLoading(true);
    try {
      await unlinkPhoneProvider();
      onVerified(''); // Indicate that the phone number has been unlinked
      onClose();
    } catch (err: unknown) {
      console.error('Error unlinking phone number:', err);
      setError(err instanceof Error ? err.message : 'Failed to unlink phone number.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format phone number for display (hide all but last 4 digits)
  const formatPhoneNumber = (phone: string) => {
    return phone.replace(/\d(?=\d{4})/g, "*");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[425px]">
        {showWhatsAppStep ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('phoneVerification.whatsappSetup.title')}</DialogTitle>
              <DialogDescription>
                {t('phoneVerification.whatsappSetup.description')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="p-4 rounded-md bg-muted mb-4 w-full">
                <p className="text-sm mb-3">
                  {t('phoneVerification.whatsappSetup.instructions')}
                </p>
                <Button 
                  className="w-full sm:w-auto mb-3"
                  onClick={() => window.open(getWhatsAppLink(), '_blank')}
                >
                  <Phone className="mr-2 h-4 w-4" />
                  {t('phoneVerification.whatsappSetup.openWhatsApp')}
                </Button>
              </div>
            </div>
          </>
        ) : !isCodeSent ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('phoneVerification.enterPhone.title')}</DialogTitle>
              <DialogDescription>
                {t('phoneVerification.enterPhone.description')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex gap-2 mb-4">
                <Popover open={isCountrySelectOpen} onOpenChange={setIsCountrySelectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isCountrySelectOpen}
                      className="w-[110px] justify-between px-3"
                    >
                      {countryCode
                        ? countries.find((country) => country.code === countryCode)?.flag +
                          ' ' +
                          countryDialCode
                        : '+1'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-0">
                    <Command>
                      <CommandInput placeholder={t('phoneVerification.enterPhone.searchCountry')} />
                      <CommandEmpty>{t('phoneVerification.enterPhone.noCountryFound')}</CommandEmpty>
                      <CommandList>
                        <CommandGroup>
                          {countries.map((country) => (
                            <CommandItem
                              key={country.code}
                              value={country.code}
                      onSelect={(currentValue) => {
                                handleCountryCodeChange(currentValue as CountryCode);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  tempCountryCode === country.code ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <span className="mr-2">{country.flag}</span>
                              <span>{country.label}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Input
                  className="flex-1"
                  placeholder={t('phoneVerification.enterPhone.phonePlaceholder')}
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  disabled={isLoading}
                />
              </div>
              <Button 
                onClick={handleSendCode} 
                disabled={!phoneNumber || isLoading || timeLeft > 0 || !isValidPhoneNumber(phoneNumber, countryCode)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('phoneVerification.enterPhone.sending')}
                  </>
                ) : timeLeft > 0 ? t('phoneVerification.enterPhone.resendIn', { seconds: timeLeft }) : t('phoneVerification.enterPhone.sendCode')}
              </Button>
              {currentPhoneNumber && (
                <Button
                  variant="destructive"
                  onClick={handleUnlinkPhoneNumber}
                  disabled={isLoading}
                >
                  {t('phoneVerification.enterPhone.unlinkPhone')}
                </Button>
              )}
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
            <div id="recaptcha-container"></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('phoneVerification.verifyCode.title')}</DialogTitle>
              <DialogDescription>
                {t('phoneVerification.verifyCode.description', { phoneNumber: formatPhoneNumber(`${countryDialCode}${phoneNumber}`) })}
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex flex-col items-center py-4">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={setVerificationCode}
                className="mb-4"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                {!isVerified && (
                  <Button 
                    variant="secondary" 
                    onClick={() => setIsCodeSent(false)}
                  >
                    {t('phoneVerification.verifyCode.back')}
                  </Button>
                )}
                {!isVerified ? (
                  <Button 
                    onClick={handleVerifyCode} 
                    disabled={verificationCode.length !== 6 || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('phoneVerification.verifyCode.verifying')}
                      </>
                    ) : (
                      t('phoneVerification.verifyCode.verify')
                    )}
                  </Button>
                ) : (
                  <Button onClick={onClose}>
                    {t('phoneVerification.verifyCode.close')}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};